import { useForm } from '@shared/composables/useForm';
import { useRoute } from '@shared/composables/useRoute';
import { usePayload } from '@shared/composables/usePayload';
import { useInjectErrorState } from '@shared/composables/useProvideErrorState';
import { useWithValidationConfig } from '@shared/composables/useWithValidationConfig';
import { type IOwner, Owner } from '@app/land-hold/owner/models/Owner';
import type { FormOptions } from '@shared/types/Form';
import type { ListResponse } from '@shared/types/ApiResponse.ts';
import type { IPayoutEvent } from '@app/purchase/bundle/models/PayoutEvent.ts';
import { useFetchListData } from '@shared/composables/useFetchListData.ts';
import { useRouter } from '@shared/composables/useRouter.ts';

export function useOwnerRepository<T extends IOwner = IOwner>() {
    const command = () => {
        const store = (data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                Owner.routes().store,
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const update = (id: string, data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                {
                    url: useRoute().build(Owner.routes().update, {
                        id,
                    }),
                    method: Owner.routes().update.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const destroy = <U extends { id: string }>(id: string, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<U>({ id } as U).precognition(
                {
                    url: useRoute().build(Owner.routes().destroy, {
                        id,
                    }),
                    method: Owner.routes().destroy.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.submit(options);
            }

            return { form, execute };
        };

        return {
            store,
            update,
            destroy,
        };
    };

    const query = () => {
        const buildGetUrl = (id: string) => {
            return { endpoint: useRoute().build(Owner.routes().show, { id }) };
        };

        const listEligiblePayoutEvents = (ownerId: string) => {
            const { data, processing } = useRouter().get<ListResponse<IPayoutEvent>>(route('owners.eligible-bundles', { id: ownerId }));
            return { data, processing };
        };

        const list = () => {
            const { data: owners, processing: fetchingOwners } = useFetchListData<Owner>(useRoute().build(Owner.routes().index), 'appSection@owner::IndexOwnersPage', (payload) => Owner.from(payload));
            return { owners, fetchingOwners };
        };

        return {
            buildGetUrl,
            listEligiblePayoutEvents,
            list,
        };
    };

    return {
        command: command(),
        query: query(),
    };
}
